import { teamsService } from "@/services";
import type { OrgIntent } from "@/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "./queryKeys";

export function useTeams(
	opts: { includeMembers?: boolean; includeArchived?: boolean } = {},
) {
	return useQuery({
		queryKey: qk.teams(opts),
		queryFn: ({ signal }) => teamsService.list(opts, signal),
	});
}

export function useTeam(teamId: string | null) {
	return useQuery({
		queryKey: qk.team(teamId ?? ""),
		queryFn: ({ signal }) => teamsService.get(teamId as string, signal),
		enabled: !!teamId,
	});
}

/**
 * Team mutations.
 *
 * Each invalidates BOTH the team lists and the roster: a seat carries its team,
 * so assigning someone leaves the People screen showing the old one. Members
 * were the thing most likely to go stale here, exactly as seat counts are for
 * useMemberMutations.
 */
export function useTeamMutations() {
	const qc = useQueryClient();
	const invalidate = () => {
		qc.invalidateQueries({ queryKey: qk.teams() });
		qc.invalidateQueries({ queryKey: qk.members() });
	};

	return {
		create: useMutation({
			mutationFn: (input: {
				slug: string;
				name: string;
				description?: string | null;
				intent?: OrgIntent;
			}) => teamsService.create(input),
			onSuccess: invalidate,
		}),
		update: useMutation({
			mutationFn: ({
				teamId,
				patch,
			}: {
				teamId: string;
				patch: Partial<{
					name: string;
					description: string | null;
					intent: OrgIntent;
				}>;
			}) => teamsService.update(teamId, patch),
			onSuccess: invalidate,
		}),
		archive: useMutation({
			mutationFn: (teamId: string) => teamsService.archive(teamId),
			onSuccess: invalidate,
		}),
		addMember: useMutation({
			mutationFn: ({
				teamId,
				member,
			}: { teamId: string; member: { memberId?: string; email?: string } }) =>
				teamsService.addMember(teamId, member),
			onSuccess: invalidate,
		}),
		removeMember: useMutation({
			mutationFn: ({
				teamId,
				memberId,
			}: { teamId: string; memberId: string }) =>
				teamsService.removeMember(teamId, memberId),
			onSuccess: invalidate,
		}),
	};
}

/**
 * The org's own intent. Invalidates the ORG rather than the teams: it lives on
 * /api/org/me, and a stale one would keep showing the previous goal on Settings
 * after saving a new one.
 */
export function useOrgIntentMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (intent: OrgIntent) => teamsService.setOrgIntent(intent),
		onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
	});
}

/**
 * The domain labels an intent may name.
 *
 * The taxonomy changes about never and is identical for every tenant, so this
 * is fetched once and kept — refetching it on every dialog open would be a
 * request per keystroke's worth of nothing.
 */
export function useDomains() {
	return useQuery({
		queryKey: qk.domains,
		queryFn: ({ signal }) => teamsService.domains(signal),
		staleTime: Number.POSITIVE_INFINITY,
	});
}
